import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { scopeToSite } from '../../lib/siteScope';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import Button from '../../components/ui/Button';
import { toast } from '../../components/ui/Toast';
import { IconDownload, IconTool } from '@tabler/icons-react';

export default function ServiceDueReport() {
  const { orgId, userProfile, role } = useAuth();
  const siteId = userProfile?.siteId;
  const navigate = useNavigate();
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const snap = await getDocs(collection(db, 'organisations', orgId, 'machines'));
        const all = scopeToSite(snap.docs.map(d => ({ id: d.id, ...d.data() })), role, siteId);
        const withService = all.filter(m => m.nextServiceHours || m.nextServiceDate).map(m => {
          const hoursRemaining = m.nextServiceHours && m.currentHours ? m.nextServiceHours - m.currentHours : null;
          const overdue = hoursRemaining !== null && hoursRemaining <= 0;
          const approaching = hoursRemaining !== null && hoursRemaining > 0 && hoursRemaining <= 50;
          return { ...m, hoursRemaining, overdue, approaching };
        });
        withService.sort((a, b) => {
          if (a.overdue && !b.overdue) return -1;
          if (!a.overdue && b.overdue) return 1;
          return (a.hoursRemaining || 999) - (b.hoursRemaining || 999);
        });
        setMachines(withService);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, [orgId]);

  const overdue = machines.filter(m => m.overdue).length;
  const approaching = machines.filter(m => m.approaching).length;

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <PageHeader
          title="Service due report"
          subtitle="Machines approaching or overdue service"
          actions={<Button variant="secondary" icon={<IconDownload size={14} stroke={1.5} />} onClick={() => toast.info('PDF export coming soon')}>Export PDF</Button>}
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 20 }}>
          {[
            { label: 'Machines tracked', value: machines.length, colour: 'var(--color-text-primary)' },
            { label: 'Overdue', value: overdue, colour: overdue > 0 ? 'var(--color-danger)' : 'var(--color-success)' },
            { label: 'Due within 50hrs', value: approaching, colour: approaching > 0 ? 'var(--color-warning)' : 'var(--color-success)' },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '14px 16px' }}>
              <div style={{ fontSize: 28, fontWeight: 400, color: s.colour, marginBottom: 4 }}>{s.value}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{s.label}</div>
            </div>
          ))}
        </div>
        {loading ? <LoadingSkeleton rows={5} card /> : machines.length === 0 ? (
          <Card><EmptyState icon={<IconTool size={28} stroke={1.5} />} title="No service schedules set" message="Add service schedules to machines to track them here" /></Card>
        ) : (
          <Card padding="0">
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', padding: '8px 16px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)' }}>
              {['Machine', 'Current hours', 'Service due', 'Remaining'].map(h => (
                <div key={h} style={{ fontSize: 10, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{h}</div>
              ))}
            </div>
            {machines.map((m, i) => (
              <div
                key={m.id}
                onClick={() => navigate('/machines/' + m.id)}
                style={{
                  display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr',
                  padding: '11px 16px',
                  borderBottom: i < machines.length - 1 ? '0.5px solid var(--color-border-light)' : 'none',
                  cursor: 'pointer', alignItems: 'center',
                  background: m.overdue ? 'var(--color-danger-light)' : m.approaching ? 'var(--color-warning-light)' : 'transparent',
                }}
                onMouseEnter={e => !m.overdue && !m.approaching && (e.currentTarget.style.background = 'var(--color-bg-secondary)')}
                onMouseLeave={e => !m.overdue && !m.approaching && (e.currentTarget.style.background = 'transparent')}
              >
                <div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-primary)' }}>{m.makeLabel} {m.modelLabel}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{m.siteName}</div>
                </div>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{m.currentHours ? m.currentHours.toLocaleString() + ' hrs' : '—'}</div>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{m.nextServiceHours ? m.nextServiceHours.toLocaleString() + ' hrs' : '—'}</div>
                <div style={{ fontSize: 13, fontWeight: 400, color: m.overdue ? 'var(--color-danger)' : m.approaching ? 'var(--color-warning)' : 'var(--color-success)' }}>
                  {m.hoursRemaining !== null ? (m.overdue ? 'Overdue by ' + Math.abs(m.hoursRemaining) + ' hrs' : m.hoursRemaining + ' hrs') : '—'}
                </div>
              </div>
            ))}
          </Card>
        )}
      </div>
    </Layout>
  );
}